import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  HostListener,
  ElementRef,
} from '@angular/core';
import { TuiHostedDropdownComponent } from '@taiga-ui/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'src/app/models/menu-item/menu-item.interface';
import { LanguageService } from 'src/app/services/language/language.service';
import { Language } from 'src/app/models/language/language.interface';
import { LanguageEnum } from 'src/app/enum/language.enum';
import { LocalStorageKeys } from 'src/app/enum/local-storage-keys.enum';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { Permission } from 'src/app/enum/permission.enum';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ROUTES } from 'src/app/constants/routes';
import { ModalLanguageChangeComponent } from '../modal-language-change/modal-language-change.component';
import { ModalConfirmLogoutComponent } from '../modal-confirm-logout/modal-confirm-logout.component';
import { AccountInfo } from '@azure/msal-browser';
import { Subject, takeUntil } from 'rxjs';

export interface UserItem {
  label: string;
  icon: string;
  id: string;
}

enum UserItemId {
  LANGUAGE = 'LANGUAGE',
  LOGOUT = 'LOGOUT',
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild(TuiHostedDropdownComponent) component?: TuiHostedDropdownComponent;

  private readonly destroy$ = new Subject<void>();
  open = false;
  modalRef?: BsModalRef;
  language: string;
  availableLanguages: Language[] = [];
  menuItems: MenuItem[] = [];
  showUserItems: boolean = false;

  AccountInfo: AccountInfo | null = null;
  UserInicials: string | null = '';

  userItems: UserItem[] = [
    {
      label: 'USER_ITEM.LANGUAGE',
      icon: 'tuiIconGlobe',
      id: UserItemId.LANGUAGE,
    },
    {
      label: 'USER_ITEM.LOGOUT',
      icon: 'tuiIconLogOut',
      id: UserItemId.LOGOUT,
    },
  ];

  private readonly BASE_MENU: MenuItem[] = [
   // { label: 'menu.home', link: ROUTES.HOME },
    { label: 'menu.personal', link: ROUTES.OPERATIONAL_STAFF },
    { label: 'menu.assigner', link: ROUTES.ASSIGNER },
    { label: 'menu.service_manager', link: ROUTES.SERVICE_MANAGER },
    //{ label: 'menu.providers', link: ROUTES.PROVIDERS }, [ER - 10/09/2025 - Esta pantalla de momento me han dicho de comentarla - Solo hay datos fake]
    // { label: 'menu.clients', link: ROUTES.CLIENTS }, [ER - 19/03/2025 - Esta pantalla de momento me han dicho de comentarla]
    { label: 'menu.commercial', link: ROUTES.COMMERCIAL },
    //{ label: 'menu.sweeper_car', link: ROUTES.SWEPPER_CAR }, [ER - 10/09/2025 - Esta pantalla de momento me han dicho de comentarla - Solo hay datos fake]
    { label: 'menu.pool_manager', link: ROUTES.POOL_MANAGER },
    { label: 'menu.expenses', link: ROUTES.EXPENSES },
  ];

  private readonly PERMISSION_MAP: Record<string, Permission> = {
    'menu.home': Permission.HOME,
    'menu.personal': Permission.PERSONAL,
    'menu.assigner': Permission.ASSIGNER,
    'menu.service_manager': Permission.SERVICE_MANAGER,
    'menu.providers': Permission.PROVIDERS,
    // 'menu.clients': Permission.CLIENTS, [ER - 19/03/2025 - Esta pantalla de momento me han dicho de comentarla]
    'menu.commercial': Permission.COMMERCIAL,
    'menu.sweeper_car': Permission.SWEEPERCAR,
    'menu.pool_manager': Permission.POOL_MANAGER,
    'menu.expenses': Permission.EXPENSES,
  };

  constructor(
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly modalService: BsModalService,
    private readonly languageService: LanguageService,
    private readonly localStorageService: LocalStorageService,
    private authService: AuthService,
    private permissionsService: PermissionsService,
    private bsModalService: BsModalService,
    private eRef: ElementRef
  ) {
    this.language =
      this.localStorageService.get<string>(LocalStorageKeys.LANGUAGE) ||
      LanguageEnum.DEFAULT;
  }

  getInicials(name: string): string {
    const palabras = name.trim().split(/\s+/); // divide por espacios, incluso múltiples
    const iniciales = palabras
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join('');
    return iniciales;
  }

  ngOnInit(): void {
    this.setLanguage(this.language);
    this.availableLanguages = this.languageService.getLanguages();
    this.loadMenuPermissions();
    
    this.authService.getUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: AccountInfo | null) => {
        this.AccountInfo = data;
        if (this.AccountInfo) {
          this.UserInicials = this.getInicials(this.AccountInfo.name ?? '');
        }
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showUserItems) {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-card-container')) {
        this.showUserItems = false;
      }
    }
  }

  private loadMenuPermissions(): void {
    this.permissionsService
      .arePermissionsLoaded()
      .subscribe((isPermissionsLoaded: boolean) => {
        if (isPermissionsLoaded) {
          this.permissionsService
            .getPermissions()
            .subscribe((permissions: Permission[]) => {
              this.configureMenu(permissions);
            });
        }
      });
  }

  private configureMenu(permissions: Permission[]): void {
    this.menuItems = this.BASE_MENU.filter((item: MenuItem) => {
      const permission = this.PERMISSION_MAP[item.label];
      return permission && permissions.includes(permission);
    });
  }

  onClick(): void {
    this.open = false;
    this.component?.nativeFocusableElement?.focus();
  }

  toggleDropdown(): void {
    this.open = !this.open;
  }

  logout(usePopup = false): void {
    this.authService.logout(usePopup);
  }

  openModalLogout(template: TemplateRef<void>): void {
    this.modalRef = this.modalService.show(template, {
      ignoreBackdropClick: true,
      class: 'gray modal-lg mt_20p',
    });
  }

  redirect(route: string): void {
    this.router.navigate([route]);
  }

  selectLanguage(language: string): void {
    if (language !== this.language) {
      this.setLanguage(language);
      this.toggleDropdown();
    }
  }

  private setLanguage(language: string): void {
    this.language = language;
    this.localStorageService.set(LocalStorageKeys.LANGUAGE, language);
    this.translate.use(this.language);
  }

  userItemAction(userItemId: string) {
    switch (userItemId) {
      case UserItemId.LANGUAGE:
        this.openLanguageChangeModal();
        break;
      case UserItemId.LOGOUT:
        this.openLogoutModal();
        break;
    }
  }

  openLanguageChangeModal() {
    let config = {
      class: 'modal-class-template-400',
      backdrop: true,
    };

    const LanguageModal = this.bsModalService.show(
      ModalLanguageChangeComponent,
      config
    );
  }

  openLogoutModal() {
    let config = {
      class: 'modal-class-template-400',
      backdrop: true,
    };

    const LogoutModal = this.bsModalService.show(
      ModalConfirmLogoutComponent,
      config
    );

    LogoutModal?.content?.onClose.subscribe((result) => {
      if (result) {
        this.logout();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
